import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  useGetClientTenantsQuery,
  useGetTenantClientQuery,
} from 'api/client.api';
import {
  useGetParameterListsQuery,
  useGetTemplateSetsQuery,
} from 'api/parameter.api';
import {
  useGetTenantQuery,
  useUpdateTenantGeneralInfoMutation,
} from 'api/tenant.api';
import styles from 'assets/jss/material-dashboard-pro-react/views/tenantStyle.js';
import Button from 'components/CustomButtons/Button.js';
import CustomInput from 'components/CustomInput/CustomInput.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PromptAlert } from '../../features/ux/RouterPrompt';

const useStyles = makeStyles(styles);

export default function GeneralTab({
  onSuccessTab,
  tab,
  setDirtyTabStatus,
}) {
  const classes = useStyles();
  const { data: tenant } = useGetTenantQuery();
  const [updateGeneralInfo] = useUpdateTenantGeneralInfoMutation();
  const { data: parameterSets } = useGetParameterListsQuery();
  const { data: templateSets } = useGetTemplateSetsQuery();
  const { data: currentClient } = useGetTenantClientQuery();
  const { data: clientTenants } = useGetClientTenantsQuery(currentClient?._id);

  const [viewIsDirty, setViewIsDirty] = useState(false);
  const [showWarningAlert, setShowWarningAlert] = useState(false);

  const [tenantLabel, setTenantLabel] = useState(tenant?.tenant_label);
  const [tenantLabelState, setTenantLabelState] = useState('');
  const [tenantLabelError, setTenantLabelError] = useState('');
  const [tenantName, setTenantName] = useState(tenant?.tenant_name);
  const [tenantNameState, setTenantNameState] = useState('');
  const [tenantNameError, setTenantNameError] = useState('');
  const [description, setDescription] = useState(tenant?.description);
  const [descriptionState, setDescriptionState] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [taxId, setTaxId] = useState(tenant?.tax_id);
  const [taxIdState, setTaxIdState] = useState('');
  const [taxIdError, setTaxIdError] = useState('');

  const [parameterSet, setParameterSet] = useState(tenant?.parameters);
  const [parameterSetState, setParameterSetState] = useState('');
  const [parameterSetError, setParameterSetError] = useState('');
  const [templateSet, setTemplateSet] = useState(tenant?.templates);
  const [templateSetState, setTemplateSetState] = useState('');
  const [templateSetError, setTemplateSetError] = useState('');

  const verifyLength = (value, original, length, required = true) => {
    if (value && value.length >= length) {
      if (value !== original) setViewIsDirty(true);
      return true;
    } else if (!required && (!value || value.length === 0)) {
      if (value !== original) setViewIsDirty(true);
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (setDirtyTabStatus) {
      setDirtyTabStatus(viewIsDirty);
    }
  }, [viewIsDirty]);

  const verifyLabel = (newLabel) => {
    if (verifyLength(newLabel, tenantLabel, 3)) {
      if (tenant.tenant_label !== newLabel) {
        const index = clientTenants.findIndex(
          (ct) => ct.tenant_label === newLabel
        );
        if (index < 0) {
          setTenantLabelError('');
          setTenantLabelState('success');
          return true;
        } else {
          setTenantLabelError(
            'Duplicate label already exists. Enter a different label.'
          );
          setTenantLabelState('error');
          return false;
        }
      } else {
        setTenantLabelError('');
        setTenantLabelState('success');
        return true;
      }
    } else {
      setTenantLabelError(
        'Field must contain at least 3 alphanumeric or special characters'
      );
      setTenantLabelState('error');
      return false;
    }
  };

  const verifyName = (newName) => {
    if (verifyLength(newName, tenantName, 3)) {
      if (tenant.tenant_name !== newName) {
        const index = clientTenants.findIndex(
          (ct) => ct.tenant_name === newName
        );
        if (index < 0) {
          setTenantNameError('');
          setTenantNameState('success');
          return true;
        } else {
          setTenantNameError(
            'Duplicate name already exists. Enter a different name.'
          );
          setTenantNameState('error');
          return false;
        }
      } else {
        setTenantNameError('');
        setTenantNameState('success');
        return true;
      }
    } else {
      setTenantNameError(
        'Field must contain at least 3 alphanumeric or special characters'
      );
      setTenantNameState('error');
      return false;
    }
  };

  const revertForm = () => {
    setTenantLabel(tenant.tenant_label);
    setTenantName(tenant.tenant_name);
    setDescription(tenant.description);
    setParameterSet(tenant.parameters);
    setTemplateSet(tenant.templates);
    setTaxId(tenant.tax_id);
    setTenantLabelState('');
    setTenantNameState('');
    setDescriptionState('');
    setParameterSetState('');
    setTemplateSetState('');
    setTaxIdState('');
    setTenantLabelError('');
    setTenantNameError('');
    setDescriptionError('');
    setParameterSetError('');
    setTemplateSetError('');
    setTaxIdError('');
    setViewIsDirty(false);
  };

  const parameterChangeHandler = (event) => {
    if (event.target.value !== parameterSet) {
      setParameterSet(event.target.value);
      setParameterSetState('success');
      setParameterSetError('');
      setViewIsDirty(true);
    }
  };

  const templateChangeHandler = (event) => {
    if (event.target.value !== templateSet) {
      setTemplateSet(event.target.value);
      setTemplateSetState('success');
      setTemplateSetError('');
      setViewIsDirty(true);
    }
  };

  const saveChanges = async () => {
    let error = false;

    if (tenantLabel.trim() === '') {
      setTenantLabelState('error');
      setTenantLabelError('Label is required.');
      error = true;
    }

    if (tenantName.trim() === '') {
      setTenantNameState('error');
      setTenantNameError('Name is required.');
      error = true;
    }

    if (error) {
      return;
    }

    try {
      const updatedTenant = cloneDeep(tenant);
      updatedTenant.tenant_label = tenantLabel;
      updatedTenant.tenant_name = tenantName;
      updatedTenant.description = description;
      updatedTenant.parameters = parameterSet;
      updatedTenant.templates = templateSet;
      updatedTenant.tax_id = taxId;

      await updateGeneralInfo({ tenantGeneralInfo: updatedTenant }).unwrap();
      setViewIsDirty(false);

      toast.success('Changes saved successfully', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });

      if (onSuccessTab) {
        onSuccessTab(tab.name);
      }
    } catch (err) {
      toast.error('An error occurred while ' + 'trying to save the changes', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });
      console.error('Error saving tenant data:', err);
    }
  };

  const parameterSelection = () => {
    const seen = {};
    const parameterList = parameterSets
      .filter((set) => !seen[set.name] && (seen[set.name] = true))
      .map((set, key) => (
        <MenuItem key={key} value={set.name}>
          {set.name}
        </MenuItem>
      ));

    return (
      <Select
        variant='standard'
        className={classes.select}
        labelId='parameter-select-label'
        id='parameter-select'
        value={parameterSet}
        onChange={(event) => {
          parameterChangeHandler(event);
        }}
      >
        <MenuItem key={-1} value={'None'}>
          None
        </MenuItem>
        {parameterList}
      </Select>
    );
  };

  const templateSelection = () => {
    const seen = {};
    const templateList = templateSets
      .filter((set) => !seen[set.name] && (seen[set.name] = true))
      .map((set, key) => (
        <MenuItem key={key} value={set.name}>
          {set.name}
        </MenuItem>
      ));

    return (
      <Select
        variant='standard'
        className={classes.select}
        labelId='template-select-label'
        id='template-select'
        value={templateSet}
        onChange={(event) => {
          templateChangeHandler(event);
        }}
      >
        <MenuItem key={-1} value={'None'}>
          None
        </MenuItem>
        {templateList}
      </Select>
    );
  };

  const hasErrors = useCallback(() => {
    return [
      tenantLabelState,
      tenantNameState,
      descriptionState,
      taxIdState,
      parameterSetState,
      templateSetState,
    ].some((state) => state === 'error');
  }, [
    tenantLabelState,
    tenantNameState,
    descriptionState,
    taxIdState,
    parameterSetState,
    templateSetState,
  ]);

  return (
    <GridContainer>
      {showWarningAlert && (
        <PromptAlert
          message='You have unsaved changes. Are you sure you want to discard them?'
          title='Discard Changes'
          onConfirm={() => {
            revertForm();
            setShowWarningAlert(false);
          }}
          onCancel={() => setShowWarningAlert(false)}
        />
      )}
      <GridItem xs={8}>
        <CustomInput
          helperText={
            tenantLabelState === 'error'
              ? tenantLabelError ?? 'An error has occurred'
              : false
          }
          success={tenantLabelState === 'success'}
          error={tenantLabelState === 'error'}
          labelText='Label'
          id='label'
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            value: tenantLabel ?? '',
            onChange: (event) => {
              if (verifyLabel(event.target.value)) {
                setTenantLabelState('success');
              } else {
                setTenantLabelState('error');
              }
              setTenantLabel(event.target.value);
            },
            autoComplete: 'off',
          }}
        />
        <CustomInput
          helperText={
            tenantNameState === 'error'
              ? tenantNameError ?? 'An error has occurred'
              : false
          }
          success={tenantNameState === 'success'}
          error={tenantNameState === 'error'}
          labelText='Name'
          id='display'
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            value: tenantName ?? '',
            onChange: (event) => {
              if (verifyName(event.target.value)) {
                setTenantNameState('success');
              } else {
                setTenantNameState('error');
              }
              setTenantName(event.target.value);
            },
            autoComplete: 'off',
          }}
        />
        <CustomInput
          helperText={
            descriptionState === 'error'
              ? descriptionError ?? 'An error has occurred'
              : false
          }
          success={descriptionState === 'success'}
          error={descriptionState === 'error'}
          labelText='Description'
          id='description'
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            value: description ?? '',
            onChange: (event) => {
              if (verifyLength(event.target.value, description, 3, false)) {
                setDescriptionState('success');
                setDescriptionError('');
              } else {
                setDescriptionState('error');
                setDescriptionError(
                  'Field must contain at least 3 alphanumeric or special characters'
                );
              }
              setDescription(event.target.value);
            },
            autoComplete: 'off',
          }}
        />
        <CustomInput
          helperText={
            taxIdState === 'error'
              ? taxIdError ?? 'An error has occurred'
              : false
          }
          success={taxIdState === 'success'}
          error={taxIdState === 'error'}
          labelText='TaxId'
          id='display'
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            value: taxId ?? '',
            onChange: (event) => {
              if (verifyLength(event.target.value, taxId, 9, false)) {
                setTaxIdState('success');
                setTaxIdError('');
              } else {
                setTaxIdState('error');
                setTaxIdError(
                  'Field must contain at least 9 alphanumeric or special characters'
                );
              }
              setTaxId(event.target.value);
            },
            autoComplete: 'off',
          }}
        />
      </GridItem>
      <GridItem xs={4}>
        <GridItem xs={12}>
          <FormControl variant='standard' className={classes.formControl}>
            <InputLabel
              id='parameter-select-label'
              className={classes.inputLabel}
            >
              Parameter Set
            </InputLabel>
            {parameterSelection()}
            {parameterSetState === 'error' && (
              <p className={classes.errorText}>{parameterSetError}</p>
            )}
          </FormControl>
        </GridItem>
        <GridItem xs={12}>
          <FormControl variant='standard' className={classes.formControl}>
            <InputLabel
              id='template-select-label'
              className={classes.inputLabel}
            >
              Template Set
            </InputLabel>
            {templateSelection()}
            {templateSetState === 'error' && (
              <p className={classes.errorText}>{templateSetError}</p>
            )}
          </FormControl>
        </GridItem>
      </GridItem>
      <GridItem xs={12}>
        <Button
          color='rose'
          onClick={saveChanges}
          className={classes.registerButton}
          disabled={!viewIsDirty || hasErrors()}
        >
          Save Changes
        </Button>
        <Button
          color='secondary'
          onClick={() => setShowWarningAlert(true)}
          className={classes.registerButton}
          disabled={!viewIsDirty}
        >
          Cancel
        </Button>
      </GridItem>
    </GridContainer>
  );
}
